import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useDeleteTourPlan } from '../hooks/useDeleteTourPlan'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { Modal } from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

interface DarkDeleteTourPlanModalIProps {
  isOpen: boolean
  tourPlanId: string
  facilityId: string
  name: string
  onClose: () => void
  onDelete: () => void
}

export const DarkDeleteTourPlanModal = ({
  isOpen,
  tourPlanId,
  facilityId,
  name,
  onClose,
  onDelete,
}: DarkDeleteTourPlanModalIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const { shouldEnableDeleteTourPlan } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const { deleteTourPlan, isDeleteLoading } = useDeleteTourPlan()
  const { setHighlightedDeviceIds, setTourStopDeviceIds } = useFloorPlan()

  const onSubmit = async () => {
    trackEvent('Button Press: Tour Plan Details - Delete Tour Save', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      role: me.roleName,
    })
    try {
      await deleteTourPlan(tourPlanId)
      showSuccess(tVar(t, 'floors:plan_delete_tour_success', { name }))
      setHighlightedDeviceIds(null)
      setTourStopDeviceIds(null)
      onClose()
      onDelete()
    } catch {
      showError(tVar(t, 'floors:plan_delete_tour_error', { name }))
    }
  }

  const handleCancel = () => {
    trackEvent('Button Press: Tour Plan Details - Delete Tour Cancel', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      role: me.roleName,
    })
    onClose()
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDestructive
      isSaveDisabled={isDeleteLoading || !shouldEnableDeleteTourPlan}
      isSaveLoading={isDeleteLoading}
      note={`${t('common:warning')} ${t('common:all_deletions_permanent')}`}
      onCancel={handleCancel}
      onClose={onClose}
      onSave={onSubmit}
      saveButtonLabel={t('common:delete')}
      showCancelButton
      size='compact'
      title={t('floors:plan_delete_tour_title')}
    >
      {t('floors:plan_delete_tour_body')}
    </Modal>
  )
}
